'use client';

import React, { useState } from 'react';
import Image from 'next/image';

interface AvatarProps {
  email: string;
  name?: string;
  profilePhotoUrl?: string | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export default function Avatar({ email, name, profilePhotoUrl, size = 'md', className = '' }: AvatarProps) {
  const [imageError, setImageError] = useState(false);

  // Get first letter of name or email (uppercase)
  const getInitial = () => {
    if (name) {
      return name.charAt(0).toUpperCase();
    }
    if (email) {
      return email.charAt(0).toUpperCase();
    }
    return '?';
  };

  // Size classes and dimensions
  const sizeClasses = {
    sm: { class: 'w-8 h-8 text-sm', pixels: 32 },
    md: { class: 'w-10 h-10 text-base', pixels: 40 },
    lg: { class: 'w-12 h-12 text-lg', pixels: 48 },
  };

  const sizeConfig = sizeClasses[size];

  // Generate a color based on the email (consistent color for same email)
  const getColor = () => {
    if (!email) return 'bg-gray-500';
    
    const colors = [
      'bg-blue-500',
      'bg-primary',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-yellow-500',
      'bg-red-500',
      'bg-primary-500',
      'bg-orange-500',
      'bg-cyan-500',
    ];
    
    // Simple hash function to get consistent color for same email
    let hash = 0;
    for (let i = 0; i < email.length; i++) {
      hash = email.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Show profile photo if available and no error
  if (profilePhotoUrl && !imageError && profilePhotoUrl.trim() !== '') {
    return (
      <div data-component-id="src/components/ui/Avatar.tsx:65:6" data-component-path="src/components/ui/Avatar.tsx" data-component-line="65" data-component-end-line="77" data-component-file="Avatar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`${sizeConfig.class} relative rounded-full overflow-hidden flex-shrink-0 ${className}`}
        title={name || email || 'User'}
      >
        <Image data-component-id="src/components/ui/Avatar.tsx:69:8" data-component-path="src/components/ui/Avatar.tsx" data-component-line="69" data-component-end-line="76" data-component-file="Avatar.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22src%22%3A%22%5Bvar%3AprofilePhotoUrl%5D%22%2C%22className%22%3A%22w-full%20h-full%20object-cover%22%7D"
          src={profilePhotoUrl}
          alt={name || email || 'User'}
          width={sizeConfig.pixels}
          height={sizeConfig.pixels}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      </div>
    );
  }

  // Fallback to avatar with initial
  return (
    <div data-component-id="src/components/ui/Avatar.tsx:83:4" data-component-path="src/components/ui/Avatar.tsx" data-component-line="83" data-component-end-line="88" data-component-file="Avatar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
      className={`${sizeConfig.class} ${getColor()} rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 ${className}`}
      title={name || email || 'User'}
    >
      {getInitial()}
    </div>
  );
}


